<template>
  <article
    class="relative flex h-full border-r-2 border-solid border-app-green rounded-xl md:flex-col md:border-r-0 md:border-b-2"
    :class="{ 'bg-app-green-200': isReserved || isPlaced || isReactionStop, 'bg-white': !isReserved && !isPlaced && !isReactionStop }"
  >
    <AtomLink v-if="animal" :url="animal.url" class="flex md:flex-col relative w-full">
      <div class="relative shrink-0 rounded-l-xl overflow-hidden md:rounded-l-none md:rounded-t-xl md:w-full">
        <div class="c-animal-card-picture block h-full c-animal-card-picture--portrait md:h-auto">
          <MoleculePicture
            v-if="animal.image"
            :image="animal.image"
            :class="{ 'opacity-50': isReserved || isPlaced || isReactionStop }"
            class="block w-full h-full"
            img-class="w-full h-full object-cover"
          />
          <div
            v-else
            class="c-animal-card-placeholder-image flex shrink-0 items-center justify-center h-full c-animal-card-placeholder-image--portrait"
            :class="{
              'bg-app-gray': isPlaced || isReactionStop || isReserved,
              'bg-app-green': !isPlaced && !isReactionStop && !isReserved,
            }"
          >
            <AtomIcon
              :icon="get(animal, 'animalCategory.icon.slug')"
              class="w-12 h-12 md:mx-0 md:my-0 md:w-20 md:h-20 xl:w-32 xl:h-32"
              :class="{
                'text-app-gray-silverstone': isPlaced || isReactionStop || isReserved,
                'text-app-green-200': !isPlaced && !isReactionStop && !isReserved,
              }"
            />
          </div>
        </div>
        <MoleculeFavorite class="absolute favorite--portrait" :medium="false" :large="true" :animal-id="animal.id" />
        <!-- Check if a video is present, if so, show an icon       -->
        <div v-if="animal.youtube.url" class="absolute bottom-0 right-0 mb-2 lg:mb-3 mr-2 lg:mr-3">
          <AtomIconPlay class="w-7 lg:w-10 text-white" />
        </div>
        <div class="absolute top-0 hidden md:block">
          <MoleculeBadgeInCorner v-if="isReunited" background="bg-app-lime" color="text-black">
            {{ t_('status-reunited') }}
          </MoleculeBadgeInCorner>
          <MoleculeBadgeInCorner v-else-if="isReserved" background="bg-app-gray-silverstone" color="text-white">
            {{ t_('status-reserved') }}
          </MoleculeBadgeInCorner>
          <MoleculeBadgeInCorner v-else-if="isPlaced" background="bg-app-gray-silverstone" color="text-white">
            {{ t_('status-placed') }}
          </MoleculeBadgeInCorner>
          <MoleculeBadgeInCorner v-else-if="isReactionStop" background="bg-app-gray-silverstone" color="text-white">
            {{ t_('status-reaction-stop') }}
          </MoleculeBadgeInCorner>
          <MoleculeBadgeInCorner v-else-if="isCouple" background="bg-app-green" color="text-black">
            {{ t_(`status-${animal.animalCoupleCount}`) }}
          </MoleculeBadgeInCorner>
          <MoleculeBadgeInCorner v-else-if="isAvailableSoon" background="bg-app-green" color="text-black">
            {{ t_('status-available-soon') }}
          </MoleculeBadgeInCorner>
          <MoleculeBadgeInCorner v-else-if="isMisfit" background="bg-app-lime" color="text-black">
            {{ t_('status-misfit') }}
          </MoleculeBadgeInCorner>
          <MoleculeBadgeInCorner v-else-if="isNewInShelter" background="bg-app-green" color="text-black">
            {{ t_('status-new') }}
          </MoleculeBadgeInCorner>
          <MoleculeBadgeInCorner v-else-if="isHighlighted" background="bg-app-green" color="text-black">
            {{ t_('status-highlighted') }}
          </MoleculeBadgeInCorner>
        </div>
      </div>

      <div class="flex flex-col p-4 md:flex-auto md:px-6 2xl:py-5 2xl:px-8">
        <div class="md:hidden">
          <MoleculeBadge v-if="isReunited" class="mb-1 text-xs" background="bg-app-lime" color="text-black">
            {{ t_('status-reunited') }}
          </MoleculeBadge>
          <MoleculeBadge v-else-if="isReserved" class="mb-2 text-xs" background="bg-app-gray-silverstone" color="text-white">
            {{ t_('status-reserved') }}
          </MoleculeBadge>
          <MoleculeBadge v-else-if="isPlaced" class="mb-2 text-xs" background="bg-app-gray-silverstone" color="text-white">
            {{ t_('status-placed') }}
          </MoleculeBadge>
          <MoleculeBadge v-else-if="isReactionStop" class="mb-2 text-xs" background="bg-app-gray-silverstone" color="text-white">
            {{ t_('status-reaction-stop') }}
          </MoleculeBadge>
          <MoleculeBadge v-else-if="isCouple" class="mb-1 text-xs" background="bg-app-green--persian" color="text-white">
            {{ t_(`status-${animal.animalCoupleCount}`) }}
          </MoleculeBadge>
          <MoleculeBadge v-else-if="isAvailableSoon" class="mb-1 text-xs" background="bg-app-green--persian" color="text-white">
            {{ t_('status-available-soon') }}
          </MoleculeBadge>
          <MoleculeBadge v-else-if="isMisfit" class="mb-2 text-xs" background="bg-app-lime" color="text-black">
            {{ t_('status-misfit') }}
          </MoleculeBadge>
          <MoleculeBadge v-else-if="isNewInShelter" class="mb-1 text-xs" background="bg-app-green--persian" color="text-white">
            {{ t_('status-new') }}
          </MoleculeBadge>
          <MoleculeBadge v-else-if="isHighlighted" class="mb-2 text-xs" background="bg-app-green--persian" color="text-white">
            {{ t_('status-highlighted') }}
          </MoleculeBadge>
        </div>

        <h3 v-if="!isFound" class="text-black mb-2 font-bold leading-none uppercase">
          {{ animal.title }}
        </h3>

        <div class="text-black flex flex-wrap items-center mb-1 w-full text-sm font-bold leading-tight md:mb-2">
          <span v-if="amendedGender" class="mr-1">{{ amendedGender }}</span>
          <span v-if="!isFound && amendedGender && amendedBirthday" class="mr-1" aria-hidden="true">•</span>
          <span v-if="!isFound && amendedBirthday">{{ amendedBirthday }}</span>
          <span v-if="isFound && animal.foundDate" class="mr-1" aria-hidden="true">•</span>
          <span v-if="isFound && animal.foundDate">{{ t_('found-on', { date: $d(new Date(animal.foundDate), 'short-date') }) }}</span>
        </div>

        <div v-if="isAvailable && shelterCity" class="flex items-center text-sm text-black">
          <AtomIconAppLocationPin class="inline-block mr-1 w-3 h-3 text-app-green" />
          {{ shelterCity }}
        </div>
        <div v-else-if="!isAvailable && animalCity" class="flex items-center text-sm text-black">
          <AtomIconAppLocationPin class="inline-block mr-1 w-3 h-3 text-app-green" />
          {{ animalCity }}
        </div>
      </div>
    </AtomLink>
    <div v-else>
      <MoleculeAnimalCardV2Preloader />
    </div>
  </article>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import get from 'lodash/get'
import * as animalHelper from '~/assets/animal'
import { getAnimalAttributeTranslations } from '~/assets/animal-attributes'
import { getTranslationPrefixer } from '~/composables/util'

interface Props {
  animal: any // Should ideally be a more specific type if known
  shelter: any // Should ideally be a more specific type if known
}

const props = defineProps<Props>()

const i18n = useI18n()
const { hasValue: hasGenderTranslation, getValue: genderTranslator } = getAnimalAttributeTranslations('animalGender', 'card')
const { hasValue: hasBirthdateTranslation, getValue: birthdateTranslator } = getAnimalAttributeTranslations('birthdate')

const translationPrefixerPreparer = getTranslationPrefixer(i18n)
const t_ = computed(() => translationPrefixerPreparer('AnimalCard', props.animal?.animalType || 'available').t_)

const amendedBirthday = computed(() => hasBirthdateTranslation(props.animal) && birthdateTranslator(props.animal))
const amendedGender = computed(() => hasGenderTranslation(props.animal) && t_.value(`gender-${genderTranslator(props.animal)}`))
const animalCity = computed(() => props.animal?.animalAddress?.city)
const isAvailable = computed(() => props.animal?.animalType === 'available')
const isAvailableSoon = computed(() => animalHelper.isAvailableSoon(props.animal?.availability))
const isCouple = computed(() => props.animal?.animalCoupleCount && props.animal?.animalCoupleCount !== 'one-animal')
const isFound = computed(() => props.animal?.animalType === 'found')
const isNewInShelter = computed(() => animalHelper.isNewInShelter(props.animal?.postDate, props.animal?.availability))
const isPlaced = computed(() => animalHelper.isPlaced(props.animal?.availability))
const isReactionStop = computed(() => animalHelper.isReactionStop(props.animal?.availability))
const isReserved = computed(() => animalHelper.isReserved(props.animal?.availability))
const isReunited = computed(() => ['missing', 'found'].includes(props.animal?.animalType) && props.animal?.found)
const isHighlighted = computed(() => props.animal?.animalHighlighted)
const isMisfit = computed(() => props.animal?.animalMisfit)
const shelterCity = computed(() => props.shelter?.department?.address?.city)
</script>

<style lang="scss" scoped>
// very important to set style to scoped here!
.favorite {
  top: 6px;
  left: 6px;

  &.favorite--portrait {
    @screen md {
      @apply left-auto;
      top: theme('spacing.3');
      right: theme('spacing.3');
    }
  }
}

.remove-icon {
  top: 10px;
  right: 10px;
}

.c-animal-card-picture {
  min-height: 129px;
  width: 120px;

  &.c-animal-card-picture--portrait {
    @screen md {
      @apply w-full;
    }
  }
}

.c-animal-card-placeholder-image {
  min-height: 129px;
  width: 120px;

  &.c-animal-card-placeholder-image--portrait {
    @screen md {
      @apply w-full;
      height: 222px;
    }

    @screen lg {
      height: 197px;
    }

    @screen xl {
      height: 247px;
    }

    @screen 2xl {
      height: 285px;
    }
  }
}
</style>
